import { useState } from 'react'
import { Card, Typography, Button, Space, Alert, List, Tag, Spin, Modal, theme } from 'antd'
import { CheckOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { apiService } from '@/lib/api'

const { Title, Text } = Typography

interface ChartOfAccount {
  id: string
  account_code: string
  account_name: string
  account_type: string
  description?: string
  is_active: boolean
}

export default function CoaReviewPage() {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { data: coaData, isLoading, error } = useQuery({
    queryKey: ['chart-of-accounts'],
    queryFn: () => apiService.getChartOfAccounts(),
  })

  const clearCoaMutation = useMutation({
    mutationFn: () => apiService.clearChartOfAccounts(),
    onSuccess: () => navigate('/dashboard'),
    onError: (error) => { console.error('Error clearing chart of accounts:', error); navigate('/dashboard') }
  })

  const handleProceed = () => navigate('/dashboard')
  const handleSetupLater = () => setShowDeleteModal(true)
  const confirmDelete = () => { clearCoaMutation.mutate(); setShowDeleteModal(false) }

  if (isLoading) {
    return <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center"><Spin size="large" /></div>
  }

  const accounts: ChartOfAccount[] = coaData?.success && Array.isArray(coaData.data) ? coaData.data : []

  return (
    <div className="min-h-screen p-6" style={{ background: token.colorBgLayout }}>
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-lg" style={{ background: token.colorBgContainer, border: `1px solid ${token.colorBorderSecondary}` }}>
          <div className="text-center mb-6">
            <Title level={2} className="mb-2">🎯 Chart of Accounts Review</Title>
            <Text type="secondary" className="text-lg">Our AI has created a suggested chart of accounts based on your business information</Text>
          </div>

          {error && <Alert message="Unable to load chart of accounts" description="There was an error loading your chart of accounts. You can still proceed to the dashboard." type="warning" showIcon className="mb-6" />}

          {accounts && accounts.length > 0 ? (
            <div className="mb-6">
              <Title level={4} className="mb-4"><EyeOutlined /> Generated Chart of Accounts ({accounts.length} accounts)</Title>
              <List
                dataSource={accounts}
                renderItem={(account: ChartOfAccount) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<Space><Text strong>{account.account_code}</Text><Text>{account.account_name}</Text><Tag color="blue">{account.account_type}</Tag></Space>}
                      description={account.description}
                    />
                  </List.Item>
                )}
                style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${token.colorBorderSecondary}`, borderRadius: '8px', padding: '16px', background: token.colorBgElevated }}
              />
            </div>
          ) : (
            <Alert message="No chart of accounts found" description="It looks like no chart of accounts was generated. You can proceed to the dashboard and set up your accounts manually." type="info" showIcon className="mb-6" />
          )}

          <div className="text-center">
            <Title level={4} className="mb-4">What would you like to do?</Title>
            <Space direction="vertical" size="large" className="w-full">
              <Text type="secondary" className="block mb-4">You can proceed with these suggested accounts or set them up later manually</Text>
              <Space size="large">
                <Button type="primary" size="large" icon={<CheckOutlined />} onClick={handleProceed} className="min-w-[180px]">Proceed with These Accounts</Button>
                <Button size="large" icon={<DeleteOutlined />} onClick={handleSetupLater} loading={clearCoaMutation.isPending} className="min-w-[180px]">Clear & Setup Later</Button>
              </Space>
              <Text type="secondary" className="text-sm block mt-4">Don't worry - you can always modify these accounts later in the dashboard</Text>
            </Space>
          </div>
        </Card>

        <Modal title="Clear Chart of Accounts" open={showDeleteModal} onOk={confirmDelete} onCancel={() => setShowDeleteModal(false)} confirmLoading={clearCoaMutation.isPending} okText="Yes, Clear All" cancelText="Cancel" okButtonProps={{ danger: true }}>
          <p>Are you sure you want to clear all suggested chart of accounts? This action cannot be undone, and you'll need to set up your accounts manually later.</p>
        </Modal>
      </div>
    </div>
  )
}
